import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import SEO from '../components/SEO';

const plans = [
  {
    name: 'Team',
    tag: 'For growing briefing programs',
    price: 'Custom',
    priceSuffix: 'billed annually',
    features: [
      'Up to 3 briefing centers or event teams',
      'Salesforce & Outlook integration',
      'Standard analytics dashboards',
      'Business-hours support',
    ],
    cta: 'Talk to sales',
    highlighted: false,
  },
  {
    name: 'Enterprise',
    tag: 'For global programs',
    price: 'Custom',
    priceSuffix: 'tailored to your program',
    features: [
      'Unlimited briefing centers & events',
      'All integrations · SSO · SCIM',
      'AI analytics & custom reports',
      'Dedicated CSM · 24/7 support',
      'SOC 2 · data residency options',
    ],
    cta: 'Contact enterprise',
    highlighted: true,
  },
  {
    name: 'Platform',
    tag: 'For integrations partners',
    price: 'API',
    priceSuffix: 'usage-based',
    features: [
      'REST APIs & webhooks',
      'Sandbox environments',
      'Partner portal',
      'Solution architect access',
    ],
    cta: 'Talk to partnerships',
    highlighted: false,
  },
];

const PricingPage = ({ onOpenDemo }) => {
  return (
    <>
      <SEO
        title="Pricing"
        description="BriefingIQ and EventIQ enterprise pricing plans. Custom pricing tailored to your program scale."
        path="/pricing"
        keywords={['BriefingIQ pricing', 'EventIQ pricing', 'enterprise pricing']}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Pricing', path: '/pricing' },
        ]}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">Pricing</div>
          <h1 className="headline-xl text-ink mb-6">Tailored for enterprise.</h1>
          <p className="text-lg text-ink-2 max-w-2xl mx-auto leading-relaxed">
            Every enterprise program is different. We build a plan that fits your scale, integrations, and support needs.
          </p>
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-6">
          {plans.map((p) => (
            <div
              key={p.name}
              className={`rounded-3xl p-8 lg:p-10 border ${
                p.highlighted
                  ? 'bg-[#0F172A] dark border-[#4F46E5] ring-2 ring-[#4F46E5] text-white shadow-[0_20px_40px_-15px_rgba(79,70,229,0.35)]'
                  : 'bg-white border-[var(--line-2)] text-ink'
              }`}
            >
              <div className={`text-xs uppercase tracking-[0.2em] mb-3 ${p.highlighted ? 'text-white/60' : 'text-ink-3'}`}>{p.tag}</div>
              <h3 className={`text-2xl font-semibold mb-4 ${p.highlighted ? 'text-white' : 'text-ink'}`}>{p.name}</h3>
              <div className="mb-1">
                <span className={`text-3xl font-semibold ${p.highlighted ? 'text-white' : 'text-ink'}`}>{p.price}</span>
              </div>
              <div className={`text-xs mb-6 ${p.highlighted ? 'text-white/60' : 'text-ink-3'}`}>{p.priceSuffix}</div>
              <ul className="space-y-3 mb-8">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${p.highlighted ? 'text-white' : 'text-ink'}`} strokeWidth={2} />
                    <span className={`text-sm ${p.highlighted ? 'text-white/85' : 'text-ink-2'}`}>{f}</span>
                  </li>
                ))}
              </ul>
              {p.highlighted ? (
                <Link to="/contact" data-analytics="pricing_plan_click" data-label={p.name} className="btn-invert w-full justify-center">{p.cta} <ArrowRight className="w-4 h-4" /></Link>
              ) : (
                <Link to="/contact" data-analytics="pricing_plan_click" data-label={p.name} className="btn-primary w-full justify-center">{p.cta} <ArrowRight className="w-4 h-4" /></Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-4">FAQ</div>
          <h2 className="headline-lg text-ink mb-10">Frequently asked.</h2>
          <div className="space-y-6">
            {[
              { q: 'How is BriefingIQ priced?', a: 'Pricing is based on the number of briefing centers, active users, and required integrations. We build a plan around the scope of your program.' },
              { q: 'Can I use only EventIQ?', a: 'Yes. EventIQ can be purchased on its own or with BriefingIQ. Both share the same platform, so upgrades are seamless.' },
              { q: 'Do you offer a trial?', a: 'We run a structured pilot with a scoped set of briefings or events so your team sees measurable outcomes before committing.' },
              { q: 'What about security?', a: 'SOC 2 compliant architecture, SSO/SCIM, role-based access, audit logs, and data residency options for global enterprises.' },
            ].map((f) => (
              <div key={f.q} className="border-b border-[var(--line-2)] pb-6">
                <h4 className="text-lg font-semibold text-ink mb-2">{f.q}</h4>
                <p className="text-ink-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <button onClick={onOpenDemo} className="btn-primary">
              Request a demo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default PricingPage;
